import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { BUDGET_THRESHOLDS } from '../utils/constants';

const useBudgetStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    budgets: [],
    currentBudget: null,
    loading: false,
    error: null,
    unsubscribeBudgets: null,

    // Actions
    subscribeToCurrentBudget: (familyId) => {
      if (!familyId) return;

      const currentMonth = format(new Date(), 'yyyy-MM');
      const budgetsRef = collection(db, `families/${familyId}/budgets`);
      const q = query(budgetsRef, where('month', '==', currentMonth));

      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const budgets = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          const currentBudget = budgets.length > 0 ? budgets[0] : null;
          
          set({ 
            currentBudget, 
            loading: false, 
            error: null
          });
        },
        (error) => {
          console.error('Error subscribing to current budget:', error);
          set({ error: error.message, loading: false });
        }
      );

      set({ unsubscribeBudgets: unsubscribe });
    },

    createBudget: async (familyId, userId, budgetData) => {
      if (!familyId || !userId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const budgetsRef = collection(db, `families/${familyId}/budgets`);
        
        // Check if budget for this month already exists
        const month = budgetData.month || format(new Date(), 'yyyy-MM');
        const existingBudgetQuery = query(budgetsRef, where('month', '==', month));
        const existingBudgets = await getDocs(existingBudgetQuery);
        
        if (!existingBudgets.empty) {
          set({ loading: false, error: 'Budget for this month already exists' });
          return { success: false, error: 'Budget for this month already exists' };
        }

        const monthStart = startOfMonth(new Date(month + '-01'));
        const monthEnd = endOfMonth(new Date(month + '-01'));

        const newBudget = {
          familyId,
          month,
          startDate: monthStart,
          endDate: monthEnd,
          totalBudget: budgetData.totalBudget || 0,
          totalSpent: 0,
          categories: budgetData.categories || {},
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: userId
        };

        const docRef = await addDoc(budgetsRef, newBudget);
        
        set({ loading: false, error: null });
        return { success: true, id: docRef.id };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateBudget: async (familyId, budgetId, updates) => {
      if (!familyId || !budgetId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const budgetRef = doc(db, `families/${familyId}/budgets`, budgetId);
        await updateDoc(budgetRef, {
          ...updates,
          updatedAt: new Date()
        });

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateBudgetSpending: async (familyId, budgetId, categoryId, amount, isAddition = true) => {
      if (!familyId || !budgetId || !categoryId) return { success: false, error: 'Missing required parameters' };

      try {
        const budgetRef = doc(db, `families/${familyId}/budgets`, budgetId);
        const budgetDoc = await getDoc(budgetRef);
        
        if (!budgetDoc.exists()) {
          return { success: false, error: 'Budget not found' };
        }

        const budgetData = budgetDoc.data();
        const categories = { ...budgetData.categories };
        
        if (!categories[categoryId]) {
          categories[categoryId] = { budgeted: 0, spent: 0, priority: 'need', transactions: 0 };
        }

        // Update category spending
        const currentSpent = categories[categoryId].spent || 0;
        const currentTransactions = categories[categoryId].transactions || 0;
        
        categories[categoryId].spent = isAddition 
          ? currentSpent + amount 
          : Math.max(0, currentSpent - amount);
        categories[categoryId].transactions = isAddition 
          ? currentTransactions + 1 
          : Math.max(0, currentTransactions - 1);

        // Calculate new total spent
        const totalSpent = Object.values(categories).reduce((sum, cat) => sum + (cat.spent || 0), 0);

        await updateDoc(budgetRef, {
          categories,
          totalSpent,
          updatedAt: new Date()
        });

        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    getBudgetByMonth: async (familyId, year, month) => {
      if (!familyId) return { success: false, error: 'No family ID provided' };

      try {
        const monthString = `${year}-${month.toString().padStart(2, '0')}`;
        const budgetsRef = collection(db, `families/${familyId}/budgets`);
        const q = query(budgetsRef, where('month', '==', monthString));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          return { success: false, error: 'Budget not found for this month' };
        }

        const budget = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        return { success: true, budget };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    getAllBudgets: async (familyId) => {
      if (!familyId) return { success: false, error: 'No family ID provided' };

      set({ loading: true, error: null });
      try {
        const budgetsRef = collection(db, `families/${familyId}/budgets`);
        const q = query(budgetsRef, orderBy('month', 'desc'));
        const snapshot = await getDocs(q);
        
        const budgets = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        set({ budgets, loading: false, error: null });
        return { success: true, budgets };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    deleteBudget: async (familyId, budgetId) => {
      if (!familyId || !budgetId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const budgetRef = doc(db, `families/${familyId}/budgets`, budgetId);
        await deleteDoc(budgetRef);

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    // Helper functions
    getBudgetProgress: (budget, categoryId = null) => {
      if (!budget) return { percentage: 0, status: 'safe' };

      if (categoryId) {
        const category = budget.categories[categoryId];
        if (!category || !category.budgeted) return { percentage: 0, status: 'safe' };
        
        const percentage = (category.spent / category.budgeted) * 100;
        return {
          percentage: Math.min(percentage, 100),
          status: get().getBudgetStatus(percentage)
        };
      } else {
        if (!budget.totalBudget) return { percentage: 0, status: 'safe' };
        
        const percentage = (budget.totalSpent / budget.totalBudget) * 100;
        return {
          percentage: Math.min(percentage, 100),
          status: get().getBudgetStatus(percentage)
        };
      }
    },

    getBudgetStatus: (percentage) => {
      if (percentage >= BUDGET_THRESHOLDS.EXCEEDED) return 'exceeded';
      if (percentage >= BUDGET_THRESHOLDS.CRITICAL) return 'critical';
      if (percentage >= BUDGET_THRESHOLDS.WARNING) return 'warning';
      return 'safe';
    },

    getBudgetAlerts: (budget) => {
      if (!budget) return [];

      const alerts = [];
      
      // Check total budget
      const totalProgress = get().getBudgetProgress(budget);
      if (totalProgress.status !== 'safe') {
        alerts.push({
          type: 'total',
          status: totalProgress.status,
          percentage: totalProgress.percentage,
          message: `Total budget is ${totalProgress.percentage.toFixed(0)}% spent`
        });
      }

      // Check category budgets
      Object.entries(budget.categories).forEach(([categoryId, category]) => {
        const progress = get().getBudgetProgress(budget, categoryId);
        if (progress.status !== 'safe') {
          alerts.push({
            type: 'category',
            categoryId,
            status: progress.status,
            percentage: progress.percentage,
            message: `Category budget is ${progress.percentage.toFixed(0)}% spent`
          });
        }
      });

      return alerts;
    },

    getRemainingBudget: (budget, categoryId = null) => {
      if (!budget) return 0;

      if (categoryId) {
        const category = budget.categories[categoryId];
        if (!category) return 0;
        return Math.max(0, (category.budgeted || 0) - (category.spent || 0));
      } else {
        return Math.max(0, budget.totalBudget - budget.totalSpent);
      }
    },

    clearError: () => set({ error: null }),

    cleanup: () => {
      const { unsubscribeBudgets } = get();
      if (unsubscribeBudgets) {
        unsubscribeBudgets();
        set({ unsubscribeBudgets: null });
      }
    }
  }))
);

export default useBudgetStore;

